/**
 * help.ts — the "what is this?" modal.
 *
 * A plain-language explainer of what the simulation demonstrates: what a
 * contextual bandit is, how to read the fishing metaphor, what the metrics
 * mean, and the kicker — this runs on a toaster, no GPUs required.
 *
 * Triggered by the (?) button in the top-left corner (always clickable, even
 * on the start screen). The same essay is linked from the bottom footer.
 */

/** URL of the long-form essay. Placeholder until published — swap this one
 *  constant and both the modal button + footer link update. */
export const ESSAY_URL = 'https://blog.vski.ai/posts/building-bandit-recommender.pdf';

export class HelpModal {
  readonly el: HTMLElement;
  private onKey: (e: KeyboardEvent) => void;

  constructor(onClose: () => void) {
    this.el = document.createElement('div');
    this.el.className = 'overlay help-overlay';
    this.el.innerHTML = `
      <div class="overlay-card help-card">
        <button class="help-close" aria-label="Close">×</button>
        <h2>Contextual Bandits</h2>
        <p class="help-lead">The algorithm that runs your feed. Not as hyped as GPT — but it makes billions.</p>
        <p>Addictive TikTok doomscroll, ad recommendations, streaming thumbnails, news ranking — all powered by the bandit family of algorithms. They decide what to show next, learn from whether you bite, and adapt in real time.</p>

        <h3>How to read this demo</h3>
        <ul class="help-legend">
          <li><span class="help-emoji">🎣</span> <strong>The agent</strong> is the fisher.</li>
          <li><span class="help-emoji">🐟</span> <strong>The user</strong> is the fish.</li>
        </ul>
        <p>The fisher knows nothing about where the fish are or what they want to eat. Its job: figure out the location and the right bait — by trial and error. The hard part: fish preferences <strong>drift</strong>. What worked a minute ago stops working, and the agent has to notice and re-adapt.</p>

        <h3>In business terms</h3>
        <p>The agent decides what content to show the user <em>right now</em>. When the user "bites" — clicks, watches, engages — the agent gets a reward. Every impression is a learning signal.</p>

        <h3>What the metrics mean</h3>
        <table class="metric-table">
          <tr>
            <td class="metric-name">Catch rate</td>
            <td>The agent's hit rate — like a click-through rate. Climbs as it learns.</td>
          </tr>
          <tr>
            <td class="metric-name">Cumulative regret</td>
            <td>The reward the agent gave up by not knowing the best answer from the start. Flattens as it learns.</td>
          </tr>
          <tr>
            <td class="metric-name">Explore β</td>
            <td>How hard the agent is still searching vs. exploiting what it knows. Falls as it gets confident, climbs back when the fish drift.</td>
          </tr>
          <tr>
            <td class="metric-name">Yield (kg)</td>
            <td>Total payoff accumulated over the episode.</td>
          </tr>
        </table>

        <h3>Runs on a toaster</h3>
        <p>This whole simulation is live in your browser. Bandits don't need GPUs — the math is a handful of matrix multiplications. You could train one on a Pentium 1.</p>

        <a class="essay-btn" href="${ESSAY_URL}" target="_blank" rel="noopener">Read the essay →</a>
      </div>
    `;
    // Click on the dimmed backdrop (not the card) closes; so does the × and Esc.
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) onClose();
    });
    this.el.querySelector<HTMLButtonElement>('.help-close')!.addEventListener('click', onClose);
    this.onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', this.onKey);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.el);
  }

  remove(): void {
    document.removeEventListener('keydown', this.onKey);
    this.el.remove();
  }
}
